import React, { useState, useEffect } from "react";
import { Button, Upload, message, Card } from "antd";
import { DownloadOutlined, PrinterOutlined, CloudUploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import "./assesment.css";

const { Dragger } = Upload;

const MaleAssessmentForm = () => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(null);
  
  // Get user authentication from Redux state
  const { userAuth } = useSelector((state) => ({
    userAuth: state.authentication.userAuth,
  }));

  // Fetch uploaded documents on component mount
  const fetchUploadedDocuments = React.useCallback(async () => {
    try {
      setLoadingDocuments(true);
      const token = userAuth?.obj?.token;
      
      if (!token) {
        console.error("No token available for fetching documents");
        return;
      }

      const response = await fetch("https://myfertilitydevapi-prod.azurewebsites.net/api/Patient/GetPatientDocument/5", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Fetched documents:", result);
      
      if (result && Array.isArray(result)) {
        setUploadedDocuments(result);
      } else if (result && result.data && Array.isArray(result.data)) {
        setUploadedDocuments(result.data);
      } else {
        setUploadedDocuments([]);
      }
      
    } catch (error) {
      console.error("Error fetching documents:", error);
      setUploadedDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, [userAuth?.obj?.token]);

  // Download uploaded document
  const downloadDocument = async (doc) => {
    try {
      const token = userAuth?.obj?.token;
      
      console.log("Downloading document:", doc);
      
      if (!token) {
        message.error("Authentication token not found. Please log in again.");
        return;
      }

      if (!doc.fileRef) {
        message.error("Document reference not found.");
        return;
      }

      // Try different endpoint formats
      let downloadUrl = `https://myfertilitydevapi-prod.azurewebsites.net/api/Patient/DownloadDocument/${doc.fileRef}`;
      console.log("Trying download URL:", downloadUrl);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = doc.filename || doc.fileTitle || 'Male_Assessment_Form.pdf';
      link.target = '_blank';
      
      // Add authorization header by fetching first
      let response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // If 404, try alternative endpoint format
      if (response.status === 404) {
        console.log("404 error, trying alternative URL format");
        downloadUrl = `https://myfertilitydevapi-prod.azurewebsites.net/api/Patient/DownloadDocument/svsdv/${doc.fileRef}`;
        console.log("Trying alternative URL:", downloadUrl);
        
        response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} for URL: ${downloadUrl}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Update link to use blob URL
      link.href = url;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
      message.success("Document downloaded successfully");
      
    } catch (error) {
      console.error("Error downloading document:", error);
      message.error(`Failed to download document: ${error.message}`);
    }
  };

  // Delete uploaded document
  const deleteDocument = async (doc) => {
    try {
      setDeletingDocument(doc.fileRef);
      const token = userAuth?.obj?.token;
      
      console.log("Deleting document:", doc);
      
      if (!token) {
        message.error("Authentication token not found. Please log in again.");
        return;
      }

      if (!doc.fileRef) {
        message.error("Document reference not found.");
        return;
      }

      // Show confirmation dialog
      const confirmed = window.confirm(`Are you sure you want to delete "${doc.filename || doc.fileTitle || 'this document'}"?`);
      if (!confirmed) {
        return;
      }

      // Try different delete endpoint formats
      let deleteUrl = `https://myfertilitydevapi-prod.azurewebsites.net/api/Patient/RemoveDocument/${doc.fileRef}`;
      console.log("Trying delete URL:", deleteUrl);
      
      let response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      // If 404, try alternative endpoint formats
      if (response.status === 404) {
        console.log("404 error, trying alternative delete URL format");
        
        // Try with POST method instead of DELETE
        deleteUrl = `https://myfertilitydevapi-prod.azurewebsites.net/api/Patient/DeleteDocument/${doc.fileRef}`;
        console.log("Trying POST delete URL:", deleteUrl);
        
        response = await fetch(deleteUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fileRef: doc.fileRef })
        });
        
        // If still 404, try another format
        if (response.status === 404) {
          deleteUrl = `https://myfertilitydevapi-prod.azurewebsites.net/api/Patient/RemoveDocument/svsdv/${doc.fileRef}`;
          console.log("Trying alternative format:", deleteUrl);
          
          response = await fetch(deleteUrl, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        }
      }
      
      console.log("Delete response status:", response.status);
      
      // Try to parse response as JSON if possible
      let result = null;
      try {
        const responseText = await response.text();
        if (responseText) {
          result = JSON.parse(responseText);
        }
      } catch (parseError) {
        console.log("Response is not JSON, treating as successful if status is 200");
      }
      
      console.log("Delete result:", result);
      
      // Consider successful if status is 200 or 204, regardless of response body
      if (response.ok || response.status === 200 || response.status === 204) {
        message.success("Document deleted successfully");
        
        // Remove document from local state immediately
        setUploadedDocuments(prevDocs => 
          prevDocs.filter(d => d.fileRef !== doc.fileRef)
        );
        
        // Also refresh from API to ensure consistency
        fetchUploadedDocuments();
      } else {
        throw new Error(`HTTP error! status: ${response.status} for URL: ${deleteUrl}`);
      }
      
    } catch (error) {
      console.error("Error deleting document:", error);
      
      // Even if API call fails, remove from local state if user confirmed
      if (window.confirm("The delete request failed, but would you like to remove it from the list anyway?")) {
        setUploadedDocuments(prevDocs => 
          prevDocs.filter(d => d.fileRef !== doc.fileRef)
        );
        message.warning("Document removed from list (API call failed)");
      } else {
        message.error(`Failed to delete document: ${error.message}`);
      }
    } finally {
      setDeletingDocument(null);
    }
  };

  // Load documents when component mounts
  useEffect(() => {
    if (userAuth?.obj?.token) {
      fetchUploadedDocuments();
    }
  }, [userAuth?.obj?.token, fetchUploadedDocuments]);


  const handleDownload = async () => {
    try {
      // Get user token from Redux state (like other components do)
      const token = userAuth?.obj?.token;
      
      console.log("UserAuth from Redux:", userAuth);
      console.log("Token found:", token);
      
      if (!token) {
        message.error("Authentication token not found. Please log in again.");
        return;
      }

      const response = await fetch("https://myfertilitydevapi-prod.azurewebsites.net/api/Patient/DownloadMaleFormBlank", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/pdf",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Male_Assessment_Form.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success("Male Assessment Form downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download the form. Please try again.");
    }
  };

  const handlePrint = async () => {
    try {
      // Get user token from Redux state (like other components do)
      const token = userAuth?.obj?.token;
      
      if (!token) {
        message.error("Authentication token not found. Please log in again.");
        return;
      }

      const response = await fetch("https://myfertilitydevapi-prod.azurewebsites.net/api/Patient/DownloadMaleFormBlank", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/pdf",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the PDF blob
      const blob = await response.blob();
      
      // Create object URL for the PDF
      const url = window.URL.createObjectURL(blob);
      
      // Open PDF in new window for printing
      const printWindow = window.open(url, "_blank");
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        message.error("Please allow popups to print the form.");
      }
      
      // Clean up the object URL after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
      
    } catch (error) {
      console.error("Print error:", error);
      message.error("Failed to load the form for printing. Please try again.");
    }
  };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Upload file to API
  const uploadFile = async (file) => {
    try {
      setUploading(true);
      const token = userAuth?.obj?.token;
      
      if (!token) {
        message.error("Authentication token not found. Please log in again.");
        return;
      }

      // Convert file to base64
      const base64String = await convertToBase64(file);
      
      // Remove data URL prefix (data:application/pdf;base64,)
      const base64Data = base64String.split(',')[1];
      
      const response = await fetch("https://myfertilitydevapi-prod.azurewebsites.net/api/Patient/AddDocument", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          base64: base64Data,
          filename: file.name,
          fileTitle: "Male Assessment Form",
          fileType: 5
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Upload result:", result);
      
      if (result.status || result.success) {
        message.success("Document uploaded successfully!");
        setFileList([]); // Clear file list after successful upload
        // Refresh the uploaded documents list
        fetchUploadedDocuments();
      } else {
        message.error(result.message || "Failed to upload document");
      }
      
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload document. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".pdf",
    fileList,
    beforeUpload: async (file) => {
      const isPDF = file.type === "application/pdf";
      if (!isPDF) {
        message.error("You can only upload PDF files!");
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("File must be smaller than 10MB!");
        return false;
      }
      
      // Add file to list and upload
      setFileList([file]);
      await uploadFile(file);
      
      return false; // Prevent auto upload
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  return (
    <div className="male-assessment-container">
      <div className="assessment-header">
        <h2>MALE ASSESSMENT FORM</h2>
      </div>
      
      <Card className="assessment-form-card">
        <div className="form-icon">
          <CloudUploadOutlined style={{ fontSize: "48px", color: "#00ADEF" }} />
        </div>
        
        <div className="form-title">
          <h3>Male Assessment Form</h3>
          <p>Please download, print, fill out, and upload the male assessment form below.</p>
        </div>
        
        <div className="form-actions">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            size="large"
            style={{ marginRight: "16px" }}
          >
            Download PDF
          </Button>
          <Button
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            size="large"
          >
            Print PDF
          </Button>
        </div>
      </Card>
      
      <div className="instructions-section">
        <h3>Instructions:</h3>
        <ol>
          <li>Click "Download PDF" to get the assessment form</li>
          <li>Print the form or fill it out digitally</li>
          <li>Complete all required fields on the form</li>
          <li>Upload the completed form using the upload button below</li>
          <li>Once the form is confirmed uploaded, book an appointment for review with your clinician.</li>
        </ol>
      </div>
      
      <div className="upload-section">
        <h3>Upload Completed Form:</h3>
        <div className="upload-container">
          <Dragger {...uploadProps} className="upload-dragger" disabled={uploading}>
            <div className="upload-content">
              <div className="upload-icon">
                <CloudUploadOutlined />
              </div>
              <div className="upload-text">
                <p className="main-text">
                  {uploading ? "Uploading document..." : "Click or drag PDF file to this area to upload"}
                </p>
                <p className="sub-text">Support for single PDF file upload. Max file size: 10MB</p>
              </div>
            </div>
          </Dragger>
        </div>
      </div>
      
      {/* Display uploaded documents */}
      {uploadedDocuments.length > 0 && (
        <div className="uploaded-documents-section">
          <h3>Uploaded Documents:</h3>
          <div className="documents-list">
            {uploadedDocuments.map((doc, index) => (
              <div key={index} className="document-item">
                <div className="document-info">
                  <span className="document-name">{doc.fileName || doc.filename || doc.fileTitle || `Document ${index + 1}`}</span>
                  <span className="document-date">
                    {doc.createdOn ? new Date(doc.createdOn).toLocaleDateString() : doc.uploadDate ? new Date(doc.uploadDate).toLocaleDateString() : 'Unknown date'}
                  </span>
                </div>
                <div className="document-actions">
                  <Button
                    type="primary"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => downloadDocument(doc)}
                    style={{ marginRight: "8px" }}
                  >
                    Download
                  </Button>
                  <Button
                    type="default"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => deleteDocument(doc)}
                    loading={deletingDocument === doc.fileRef}
                    style={{ marginRight: "8px", color: "#ff4d4f", borderColor: "#ff4d4f" }}
                  >
                    Delete
                  </Button>
                  <span className="status-badge success">✓ Uploaded</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Show loading state for documents */}
      {loadingDocuments && (
        <div className="documents-loading">
          <p>Loading uploaded documents...</p>
        </div>
      )}
      
      {/* Show message if no documents uploaded */}
      {!loadingDocuments && uploadedDocuments.length === 0 && (
        <div className="no-documents">
          <p>No documents uploaded yet.</p>
        </div>
      )}
    </div>
  );
};

export default MaleAssessmentForm;
